import { useEffect, useRef } from "react";

interface UseMenuScrollPaginationProps {
  onSearch: (search: string) => void;
  onScrollEnd?: () => void;
  options: any[];
  search: string;
}

export const useMenuScrollPagination = ({
  onSearch,
  onScrollEnd,
  options,
  search,
}: UseMenuScrollPaginationProps) => {
  // Keep latest callback in a ref so the scroll handler never captures a stale closure
  const onScrollEndRef = useRef(onScrollEnd);
  useEffect(() => { onScrollEndRef.current = onScrollEnd; }, [onScrollEnd]);

  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const savedScrollTopRef = useRef<number>(0);
  const isFetchingRef = useRef(false);
  const prevOptionsLengthRef = useRef(0);
  const isRestoringScrollRef = useRef(false);
  const isOpenRef = useRef(false);

  // ─── Debounced search ─────────────────────────────────────────────────────
  useEffect(() => {
    const delay = setTimeout(() => onSearch(search), 400);
    return () => clearTimeout(delay);
  }, [search, onSearch]);

  // ─── Stable scroll handler ────────────────────────────────────────────────
  const handleScroll = useRef((e: Event) => {
    if (isRestoringScrollRef.current) return;

    const target = e.target as HTMLElement;
    savedScrollTopRef.current = target.scrollTop;

    const distanceFromBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight;

    if (distanceFromBottom <= 150 && !isFetchingRef.current) {
      isFetchingRef.current = true;
      onScrollEndRef.current?.();
    }
  }).current;

  // ─── Find the MUI menu scroll container ──────────────────────────────────
  const findScrollContainer = (): HTMLElement | null => {
    const candidates = Array.from(
      document.querySelectorAll<HTMLElement>(".MuiMenu-paper, .MuiPopover-paper")
    );
    for (const el of candidates) {
      if (el.scrollHeight > el.clientHeight) return el;
    }
    const listbox = document.querySelector<HTMLElement>("[role='listbox']");
    if (listbox && listbox.scrollHeight > listbox.clientHeight) return listbox;
    return null;
  };

  const attachScrollListener = () => {
    setTimeout(() => {
      const container = findScrollContainer();
      if (container) {
        container.removeEventListener("scroll", handleScroll);
        container.addEventListener("scroll", handleScroll, { passive: true });
        scrollContainerRef.current = container;
      }
    }, 150);
  };

  const detachScrollListener = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.removeEventListener("scroll", handleScroll);
      scrollContainerRef.current = null;
    }
  };

  // ─── Unlock + restore scroll when options grow ────────────────────────────
  useEffect(() => {
    const prevLen = prevOptionsLengthRef.current;
    const newLen = options.length;

    if (newLen > prevLen && isOpenRef.current) {
      const savedTop = savedScrollTopRef.current;

      if (savedTop > 0) {
        isRestoringScrollRef.current = true;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            const container = scrollContainerRef.current ?? findScrollContainer();
            if (container) {
              scrollContainerRef.current = container;
              container.scrollTop = savedTop;
            }
            setTimeout(() => {
              isRestoringScrollRef.current = false;
              isFetchingRef.current = false;
            }, 50);
          })
        );
      } else {
        isFetchingRef.current = false;
      }
    }

    prevOptionsLengthRef.current = newLen;
  }, [options.length]);

  const handleMenuOpen = () => {
    isOpenRef.current = true;
    isFetchingRef.current = false;
    isRestoringScrollRef.current = false;
    savedScrollTopRef.current = 0;
    prevOptionsLengthRef.current = options.length;
    attachScrollListener();
  };

  const handleMenuClose = (onClose?: () => void) => {
    isOpenRef.current = false;
    isFetchingRef.current = false;
    isRestoringScrollRef.current = false;
    detachScrollListener();
    onClose?.();
  };

  useEffect(() => () => detachScrollListener(), []);

  return {
    handleMenuOpen,
    handleMenuClose,
  };
};